import type { AgendaItem, AppSnapshot, CommandRequest, ServiceType, SessionUser } from "../data/types";

const apiBase = import.meta.env.VITE_API_BASE_URL ?? "";
export const useDevelopmentFixtures = import.meta.env.DEV && import.meta.env.VITE_USE_FIXTURES === "true";

let csrfToken = "";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code = "api_error",
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { message?: string; code?: string; details?: unknown };
      message?: string;
      code?: string;
    } | null;
    throw new ApiError(
      body?.error?.message ?? body?.message ?? "No fue posible completar la solicitud.",
      response.status,
      body?.error?.code ?? body?.code,
      body?.error?.details,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function csrfFromCookie(): string {
  const prefix = "__Host-rm_csrf=";
  const entry = document.cookie.split(";").map((value) => value.trim()).find((value) => value.startsWith(prefix));
  return entry ? decodeURIComponent(entry.slice(prefix.length)) : "";
}

type ApiUser = {
  id: string;
  email: string;
  displayName: string;
  role: SessionUser["role"];
};

const permissionsByRole: Record<SessionUser["role"], SessionUser["permissions"]> = {
  owner: ["*"],
  manager: [
    "dashboard.read", "orders.read", "finance.read", "bookings.read", "confirmations.read",
    "bookings.note", "bookings.assign", "bookings.attend", "leads.followup",
    "bookings.language", "bookings.edit", "bookings.add_paid_addon", "bookings.record_payment",
    "confirmations.send", "confirmations.share", "confirmations.resend", "team.read", "audit.read", "system.read",
    "whatsapp.read", "whatsapp.reply", "whatsapp.handoff",
    "crm.read", "crm.write", "crm.reply", "crm.analytics.read",
  ],
  operator: [
    "dashboard.read", "bookings.read", "bookings.note", "bookings.attend",
    "bookings.language", "leads.followup", "confirmations.read", "confirmations.share",
    "whatsapp.read", "whatsapp.reply", "whatsapp.handoff",
    "crm.read", "crm.write", "crm.reply",
  ],
  accounting: ["dashboard.read", "orders.read", "finance.read", "bookings.read", "confirmations.read", "audit.read", "crm.analytics.read"],
};

export interface EmailSettings {
  enabled: boolean;
  fromName: string;
  fromEmail: string;
  replyTo: string;
  smtpHost: string;
  smtpPort: number;
  securityMode: "tls" | "starttls" | "none";
  smtpUsername: string;
  passwordSaved: boolean;
  autoSendConfirmations: boolean;
  version: number;
  updatedAt: string;
}

export interface ExperienceOption {
  productId?: number;
  serviceType: "balloon" | "tour" | "school" | "other";
  name: string;
}

export interface BalloonAddonOption {
  key: string;
  name: string;
  price: number;
  currency: string;
  description: string;
  perPerson: boolean;
}

export type ConfirmationCaseKey = "booking_confirmed" | "payment_received" | "booking_reminder" | "weather_reschedule" | "booking_cancelled";

export interface ConfirmationTemplate {
  id: string;
  productId?: number;
  serviceType: ExperienceOption["serviceType"];
  caseKey: ConfirmationCaseKey;
  language: "es" | "en";
  experienceName: string;
  displayName: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  version: number;
  updatedAt: string;
}

export interface CustomerCareSettings {
  assistant: {
    enabled: boolean;
    mode: "simulation" | "live";
    model: string;
    reasoning_effort: "low" | "medium" | "high";
    auto_reply_enabled: boolean;
    default_language: "es" | "en";
    max_replies_per_hour: number;
    version: number;
    updated_at: string;
  };
  whatsapp: {
    enabled: boolean;
    mode: "simulation" | "web";
    connection_status: "disabled" | "simulation" | "starting" | "qr_required" | "connecting" | "connected" | "reconnecting" | "relink_required" | "degraded" | "stopped";
    auto_reply_enabled: boolean;
    connected_phone_masked: string | null;
    last_heartbeat_at: string | null;
    last_error_code: string | null;
    pairing_qr: string | null;
    pairing_qr_expires_at: string | null;
    active_session_generation_id: string | null;
    generation_number: number | null;
    status_reason: string | null;
    last_connected_at: string | null;
    last_disconnect_at: string | null;
    last_inbound_at: string | null;
    last_outbound_at: string | null;
    last_error_at: string | null;
    reconnect_attempts: number;
    qr_expires_at: string | null;
    relink_requested_at: string | null;
    outbox: {
      pending: number;
      claimed: number;
      failed: number;
      delivery_uncertain: number;
      quarantined: number;
    };
    alert_group?: {
      subject: string;
      status: "pending" | "claimed" | "active" | "delivery_uncertain" | "failed" | "disabled";
      participant_suffixes: string[];
      external_group_jid: string | null;
      attempts: number;
      activated_at: string | null;
      last_error_code: string | null;
      updated_at: string;
      requested_subject: string | null;
      switch_status: "idle" | "pending" | "claimed" | "failed";
      switch_attempts: number;
      switch_claimed_at: string | null;
    } | null;
    internal_alerts?: {
      pending: number;
      claimed: number;
      failed: number;
      delivery_uncertain: number;
      quarantined: number;
    };
    version: number;
    updated_at: string;
  };
}

export interface WhatsAppConversation {
  id: string;
  phone_e164: string | null;
  customer_name: string | null;
  language: "es" | "en" | "unknown";
  status: "open" | "waiting_human" | "closed" | "blocked";
  ai_enabled: boolean;
  last_message_at: string;
  last_message: string | null;
  last_direction: "inbound" | "outbound" | null;
  last_message_status: string | null;
  crm_customer_id: string | null;
  crm_thread_id: string | null;
  /** Open sale of this chat (else its latest one), so Mensajes can show the case beside it. */
  crm_opportunity_id?: string | null;
}

export interface WhatsAppMessage {
  id: string;
  direction: "inbound" | "outbound";
  sender_type: "customer" | "assistant" | "user" | "system";
  body: string;
  message_type?: string;
  metadata?: Record<string, unknown> | null;
  status: string;
  error_code?: string | null;
  outbox_id?: string | null;
  /** Photo, voice note, video or document received (or sent from the phone), with its transcript. */
  media?: WhatsAppMedia | null;
  created_at: string;
}

export interface WhatsAppMedia {
  kind: "audio" | "image" | "video" | "document" | "sticker";
  mime_type: string;
  file_name?: string | null;
  byte_size: number;
  transcript?: string | null;
  transcript_status: "pending" | "running" | "done" | "failed" | "skipped";
}

function mapUser(user: ApiUser): SessionUser {
  const initials = user.displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return {
    id: user.id,
    name: user.displayName,
    email: user.email,
    role: user.role,
    initials: initials || "RM",
    permissions: permissionsByRole[user.role],
  };
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method?.toUpperCase() ?? "GET";
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");
  if (method !== "GET" && method !== "HEAD" && csrfToken) headers.set("X-CSRF-Token", csrfToken);

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${apiBase}${path}`, {
      ...init,
      credentials: "include",
      headers,
      signal: init.signal ?? controller.signal,
    });
    return await parseResponse<T>(response);
  } finally {
    window.clearTimeout(timer);
  }
}

export interface SessionResponse {
  authenticated: boolean;
  csrfToken?: string;
  user?: SessionUser;
}

export const authApi = {
  session: async (): Promise<SessionResponse> => {
    const response = await apiRequest<{ user: ApiUser; expires_at: string }>("/api/v1/auth/session");
    const token = csrfFromCookie();
    if (token) setCsrfToken(token);
    return { authenticated: true, user: mapUser(response.user), csrfToken: token };
  },
  login: async (email: string, password: string, rememberDevice: boolean): Promise<SessionResponse> => {
    const response = await apiRequest<{ user: ApiUser; csrf_token: string; expires_at: string }>("/api/v1/auth/login", {
      method: "POST", body: JSON.stringify({ email, password, remember_device: rememberDevice }),
    });
    setCsrfToken(response.csrf_token || csrfFromCookie());
    return { authenticated: true, user: mapUser(response.user), csrfToken: response.csrf_token };
  },
  recover: (email: string) =>
    apiRequest<{ message: string }>("/api/v1/auth/password-reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  reset: (token: string, password: string) =>
    apiRequest<void>("/api/v1/auth/password-reset/confirm", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),
  acceptInvitation: async (token: string, name: string, password: string): Promise<SessionResponse> => {
    const response = await apiRequest<{ user: ApiUser; csrf_token: string; expires_at: string }>("/api/v1/auth/invitations/accept", {
      method: "POST", body: JSON.stringify({ token, display_name: name, password }),
    });
    setCsrfToken(response.csrf_token || csrfFromCookie());
    return { authenticated: true, user: mapUser(response.user), csrfToken: response.csrf_token };
  },
  logout: () => apiRequest<void>("/api/v1/auth/logout", { method: "POST" }),
};

/** Downloads a file the API serves with Content-Disposition, keeping the server's file name. */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(`${apiBase}${path}`, { credentials: "include" });
  if (!response.ok) await parseResponse(response);
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plainName = disposition.match(/filename="([^"]+)"/i)?.[1];
  const fileName = encodedName ? decodeURIComponent(encodedName) : plainName ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export const erpApi = {
  snapshot: () => apiRequest<AppSnapshot>("/api/v1/ui/snapshot"),
  agenda: (from: string, to: string, service?: ServiceType) => {
    const query = new URLSearchParams({ from, to });
    if (service) query.set("service", service);
    return apiRequest<{ data: AgendaApiItem[] }>(`/api/v1/agenda?${query.toString()}`)
      .then((response) => response.data.map(mapAgendaItem));
  },
  balloonAddons: () => apiRequest<{ data: BalloonAddonOption[]; source: "wordpress" }>("/api/v1/catalog/balloon-addons")
    .then((response) => response.data),
  downloadConfirmationDocument: (confirmationId: string) =>
    downloadFile(`/api/v1/confirmations/${encodeURIComponent(confirmationId)}/document`, `confirmacion-${confirmationId}.pdf`),
  command: (command: CommandRequest) =>
    apiRequest<{ command_id: string; status: string; duplicate: boolean }>("/api/v1/commands", {
      method: "POST",
      headers: { "Idempotency-Key": command.commandId },
      body: JSON.stringify({
        command_type: command.type,
        aggregate_id: command.aggregateId,
        expected_version: command.expectedVersion,
        payload: command.payload,
      }),
    }),
  commandStatus: (commandId: string) => apiRequest<{ command_id: string; status: string; completed_at?: string; last_error?: string; response_payload?: { message?: string } }>(`/api/v1/commands/${encodeURIComponent(commandId)}`),
  bookingDetail: (sourceId: string) => apiRequest<import("../data/types").Booking>(`/api/v1/ui/bookings/${encodeURIComponent(sourceId)}`),
  shareConfirmation: (confirmationId: string, language: "es" | "en") =>
    apiRequest<{ url: string; message: string; expiresAt: string }>(
      `/api/v1/confirmations/${encodeURIComponent(confirmationId)}/share`,
      { method: "POST", body: JSON.stringify({ language }) },
    ),
};

type AgendaApiItem = {
  source_id: string;
  source_kind: "booking" | "request" | "hold";
  service_type: ServiceType;
  activity_date: string;
  activity_time: string | null;
  title: string;
  customer_name: string;
  customer_phone?: string | null;
  people_count: number | string | null;
  tentative: boolean;
  source_version?: number | string | null;
  customer_email?: string | null;
};

function mapAgendaItem(item: AgendaApiItem): AgendaItem {
  const service = item.service_type;
  const people = Number(item.people_count ?? 0);
  return {
    id: `${item.source_kind}-${item.source_id}`,
    sourceId: item.source_id,
    sourceKind: item.source_kind,
    service,
    time: item.activity_time ? item.activity_time.slice(0, 5) : null,
    type: service === "balloon" ? "Vuelo" : service === "tour" ? "Tour" : "Grupo",
    title: item.title,
    subtitle: item.customer_name,
    confirmed: item.tentative ? 0 : people,
    capacity: item.tentative ? 0 : people,
    location: "Por confirmar",
    date: item.activity_date.slice(0, 10),
    tentative: item.tentative,
    ...(item.customer_phone ? { phone: item.customer_phone } : {}),
    ...(item.customer_email ? { email: item.customer_email } : {}),
    ...(item.source_version !== null && item.source_version !== undefined ? { version: Number(item.source_version) } : {}),
  };
}

export const settingsApi = {
  load: async () => {
    const [email, experiences, templates] = await Promise.all([
      apiRequest<{ data: EmailSettings }>("/api/v1/settings/email"),
      apiRequest<{ data: ExperienceOption[] }>("/api/v1/settings/experiences"),
      apiRequest<{ data: ConfirmationTemplate[] }>("/api/v1/settings/confirmation-templates"),
    ]);
    return { email: email.data, experiences: experiences.data, templates: templates.data };
  },
  loadCustomerCare: async () => {
    const response = await apiRequest<{ data: CustomerCareSettings }>("/api/v1/settings/customer-care");
    return response.data;
  },
  saveAssistant: (value: CustomerCareSettings["assistant"]) => apiRequest<{ data: CustomerCareSettings["assistant"] }>("/api/v1/settings/customer-care/assistant", {
    method: "PUT",
    body: JSON.stringify({
      enabled: value.enabled, mode: value.mode, model: value.model,
      reasoning_effort: value.reasoning_effort,
      default_language: value.default_language, max_replies_per_hour: value.max_replies_per_hour,
      expected_version: value.version,
    }),
  }),
  saveWhatsApp: (value: CustomerCareSettings["whatsapp"]) => apiRequest<{ data: CustomerCareSettings["whatsapp"] }>("/api/v1/settings/customer-care/whatsapp", {
    method: "PUT",
    body: JSON.stringify({ expected_version: value.version }),
  }),
  setAutomaticReplies: (value: CustomerCareSettings, enabled: boolean) => apiRequest<{ data: { enabled: boolean; assistant_version: number; whatsapp_version: number } }>("/api/v1/settings/customer-care/automatic-replies", {
    method: "PUT",
    body: JSON.stringify({
      enabled,
      expected_assistant_version: value.assistant.version,
      expected_whatsapp_version: value.whatsapp.version,
    }),
  }),
  attachAlertGroup: (subject: string) => apiRequest<{ data: NonNullable<CustomerCareSettings["whatsapp"]["alert_group"]> }>("/api/v1/settings/customer-care/alert-group/attach", {
    method: "POST",
    body: JSON.stringify({ subject }),
  }),
  saveEmail: (settings: EmailSettings, smtpPassword: string) =>
    apiRequest<{ data: EmailSettings }>("/api/v1/settings/email", {
      method: "PUT",
      body: JSON.stringify({
        enabled: settings.enabled,
        from_name: settings.fromName,
        from_email: settings.fromEmail,
        reply_to: settings.replyTo,
        smtp_host: settings.smtpHost,
        smtp_port: settings.smtpPort,
        security_mode: settings.securityMode,
        smtp_username: settings.smtpUsername,
        ...(smtpPassword ? { smtp_password: smtpPassword } : {}),
        auto_send_confirmations: settings.autoSendConfirmations,
        expected_version: settings.version,
      }),
    }),
  testEmail: (recipient: string) => apiRequest<{ data: { verified: true; messageSent: boolean } }>("/api/v1/settings/email/test", {
    method: "POST",
    body: JSON.stringify({ recipient }),
  }),
  saveTemplate: (input: {
    productId?: number;
    serviceType: ExperienceOption["serviceType"];
    caseKey: ConfirmationCaseKey;
    language: "es" | "en";
    experienceName: string;
    displayName: string;
    fileName: string;
    contentBase64: string;
  }) => apiRequest<{ data: ConfirmationTemplate }>("/api/v1/settings/confirmation-templates", {
    method: "POST",
    body: JSON.stringify({
      ...(input.productId ? { product_id: input.productId } : {}),
      service_type: input.serviceType,
      case_key: input.caseKey,
      language: input.language,
      experience_name: input.experienceName,
      display_name: input.displayName,
      file_name: input.fileName,
      content_base64: input.contentBase64,
    }),
  }),
  deleteTemplate: (templateId: string) => apiRequest<void>(`/api/v1/settings/confirmation-templates/${templateId}`, { method: "DELETE" }),
  downloadTemplate: async (template: ConfirmationTemplate) => {
    const response = await fetch(`${apiBase}/api/v1/settings/confirmation-templates/${template.id}/file`, { credentials: "include" });
    if (!response.ok) await parseResponse(response);
    const url = URL.createObjectURL(await response.blob());
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = template.fileName;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
};

export const whatsappApi = {
  status: async () => {
    const response = await apiRequest<{ data: CustomerCareSettings }>("/api/v1/whatsapp/status");
    return response.data;
  },
  relink: () => apiRequest<{ data: CustomerCareSettings["whatsapp"] }>("/api/v1/whatsapp/relink", { method: "POST" }),
  list: () => apiRequest<{ data: WhatsAppConversation[]; pagination: { total: number } }>("/api/v1/whatsapp/conversations?per_page=100"),
  detail: (conversationId: string) => apiRequest<{ data: { conversation: WhatsAppConversation; messages: WhatsAppMessage[] } }>(`/api/v1/whatsapp/conversations/${conversationId}`),
  /** Conversations matching a name or phone, beyond the 100 most recent. */
  search: (term: string) => apiRequest<{ data: WhatsAppConversation[] }>(`/api/v1/whatsapp/conversations?per_page=20&search=${encodeURIComponent(term)}`),
  simulate: (body: string, language: "es" | "en") => apiRequest<{ data: { conversationId: string; messageId: string } }>("/api/v1/whatsapp/simulate", { method: "POST", body: JSON.stringify({ body, language }) }),
  /** First message to a number with no chat yet; returns the new conversation. */
  start: (phone: string, body: string, customerName?: string) => apiRequest<{ data: { conversationId: string; messageId: string } }>("/api/v1/whatsapp/conversations", { method: "POST", body: JSON.stringify({ phone, body, ...(customerName ? { customer_name: customerName } : {}) }) }),
  reply: (conversationId: string, body: string) => apiRequest<{ data: { messageId: string } }>(`/api/v1/whatsapp/conversations/${conversationId}/reply`, { method: "POST", body: JSON.stringify({ body }) }),
  /** Sends a school brochure PDF with its text in the customer's chat. */
  sendBrochure: (conversationId: string, level: string, caption: string) => apiRequest<{ data: { messageId: string } }>(`/api/v1/whatsapp/conversations/${conversationId}/brochure`, { method: "POST", body: JSON.stringify({ level, caption }) }),
  handoff: (conversationId: string) => apiRequest<void>(`/api/v1/whatsapp/conversations/${conversationId}/handoff`, { method: "POST" }),
  delegate: (conversationId: string) => apiRequest<void>(`/api/v1/whatsapp/conversations/${conversationId}/delegate`, { method: "POST" }),
  retry: (outboxId: string) => apiRequest<{ data: { messageId: string; outboxId: string } }>(`/api/v1/whatsapp/outbox/${outboxId}/retry`, { method: "POST" }),
};
